import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const CACHE = { value: null };

export const ROLE_LABELS = {
  admin: "Admin — full access, manages crew and pricing",
  sales: "Sales — accounts, quotes, holds, showroom",
  fabricator: "Fabricator — jobs, day sheet, inventory lookups",
  cnc: "CNC — jobs, slab layout, inventory lookups",
  installer: "Installer — jobs, day sheet, completion photos",
  warehouse: "Warehouse — inventory, purchase orders, suppliers",
};

export const ROLE_LIST = [
  "admin",
  "sales",
  "fabricator",
  "cnc",
  "installer",
  "warehouse",
];

export const roleName = (r) =>
  r === "cnc" ? "CNC" : r.charAt(0).toUpperCase() + r.slice(1);

/** Human label for a task/stop crew value; `group:<role>` renders as a team. */
export const crewLabel = (value, labels = {}) => {
  if (!value) return "Unassigned";
  if (typeof value === "string" && value.startsWith("group:")) {
    const key = value.slice(6);
    const base = (labels[key] || ROLE_LABELS[key] || roleName(key))
      .split("—")[0]
      .trim();
    return `${base} (team)`;
  }
  return value;
};

/** Mobile scheduling (reschedule) is limited to office managers/admins. */
export const canReschedule = (role) => role === "admin" || role === "sales";

export const clearPermissionsCache = () => {
  CACHE.value = null;
};

export const usePermissions = () => {
  const [state, setState] = useState(CACHE.value);

  useEffect(() => {
    if (CACHE.value) return;
    api
      .get("/my-permissions")
      .then(({ data }) => {
        CACHE.value = data;
        setState(data);
      })
      .catch(() => setState({ role: "", permissions: [] }));
  }, []);

  const permissions = state?.permissions || [];
  const can = (perm) => permissions.includes("*") || permissions.includes(perm);
  return { role: state?.role || "", can, loaded: !!state };
};

/** Group keys and labels straight from the server, so custom groups show up too. */
export const useRoles = () => {
  const [state, setState] = useState({ roles: ROLE_LIST, labels: ROLE_LABELS });

  useEffect(() => {
    api
      .get("/my-permissions")
      .then(({ data }) =>
        setState({
          roles: data.roles?.length ? data.roles : ROLE_LIST,
          labels: { ...ROLE_LABELS, ...(data.role_labels || {}) },
        }),
      )
      .catch(() => {});
  }, []);

  return state;
};
